import { CheckCircle2, ChevronDown, ChevronUp, KeyRound, Mail, Pencil, Search, Trash2, UserPlus, Users, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import AlertModal from '../../components/AlertModal';
import Modal from '../../components/Modal';
import ConfirmModal from '../../modals/ConfirmModal';

const emptyCreate = { userName: '', email: '', password: '', phone: '', address: '', language: 'tr' };

// Bildirim dili kodları için etiketler
const LANGUAGE_LABELS = { tr: 'Türkçe', en: 'English', de: 'Deutsch' };

export default function UserManagementPage({ users, createUser, deleteUser, updateUser, resetUserPassword }) {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newUser, setNewUser] = useState(emptyCreate);
  const [submitting, setSubmitting] = useState(false);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showResetPw, setShowResetPw] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [alert, setAlert] = useState(null);

  const activeCount = useMemo(() => users.filter((u) => u.isActive).length, [users]);
  const passiveCount = users.length - activeCount;

  const filteredUsers = useMemo(() => {
    const search = query.trim().toLowerCase();
    return users.filter((user) => {
      if (statusFilter === 'active' && !user.isActive) return false;
      if (statusFilter === 'passive' && user.isActive) return false;
      if (!search) return true;
      return (
        user.userName?.toLowerCase().includes(search) ||
        user.email?.toLowerCase().includes(search)
      );
    });
  }, [users, query, statusFilter]);

  /* Satır genişlet/daralt */
  function toggleExpand(id) {
    setExpandedId((prev) => (prev === id ? null : id));
  }

  /* Düzenleme modalını aç */
  function openEdit(user) {
    setEditTarget(user);
    setEditForm({
      userName: user.userName,
      email: user.email,
      isActive: user.isActive,
      phone: user.phone ?? '',
      address: user.address ?? '',
      language: user.language ?? 'tr',
    });
    setShowResetPw(false);
    setNewPassword('');
  }

  /* Yeni kullanıcı kaydet */
  async function submitCreate(event) {
    event.preventDefault();
    setSubmitting(true);
    try {
      const saved = await createUser(newUser);
      if (saved) {
        setNewUser(emptyCreate);
        setIsCreateOpen(false);
        setAlert({ type: 'success', message: 'Kullanıcı başarıyla oluşturuldu.' });
      }
    } catch (err) {
      setAlert({ type: 'error', message: err.message || 'Kullanıcı oluşturulamadı.' });
    } finally {
      setSubmitting(false);
    }
  }

  /* Kullanıcı bilgilerini güncelle */
  async function submitEdit(event) {
    event.preventDefault();
    setSubmitting(true);
    try {
      const saved = await updateUser(editTarget.id, editForm);
      if (saved) {
        setEditTarget(null);
        setAlert({ type: 'success', message: 'Kullanıcı bilgileri güncellendi.' });
      }
    } catch (err) {
      setAlert({ type: 'error', message: err.message || 'Güncelleme başarısız.' });
    } finally {
      setSubmitting(false);
    }
  }

  /* Şifre sıfırlama */
  async function handleResetPassword() {
    if (!newPassword || newPassword.length < 6) {
      setAlert({ type: 'error', message: 'Yeni şifre en az 6 karakter olmalıdır.' });
      return;
    }
    setSubmitting(true);
    try {
      const ok = await resetUserPassword(editTarget.id, newPassword);
      if (ok) {
        setShowResetPw(false);
        setNewPassword('');
        setAlert({ type: 'success', message: 'Şifre başarıyla sıfırlandı.' });
      }
    } catch (err) {
      setAlert({ type: 'error', message: err.message || 'Şifre sıfırlanamadı.' });
    } finally {
      setSubmitting(false);
    }
  }

  /* Silme işlemini onayla */
  async function confirmDelete() {
    const name = deleteTarget?.userName;
    try {
      await deleteUser(deleteTarget.id);
      setDeleteTarget(null);
      setAlert({ type: 'success', message: `"${name}" adlı kullanıcı silindi.` });
    } catch (err) {
      setDeleteTarget(null);
      setAlert({ type: 'error', message: err.message || 'Silme işlemi başarısız.' });
    }
  }

  return (
    <div className="um-page">
      <div className="section-header">
        <div>
          <h2>Kullanıcı Yönetimi</h2>
          <p className="muted">Alt kullanıcıları ekleyin, düzenleyin ve erişimlerini yönetin.</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setNewUser(emptyCreate); setIsCreateOpen(true); }}>
          <UserPlus /> Yeni Kullanıcı
        </button>
      </div>

      <div className="um-stats">
        <StatCard icon={<Users />} label="Toplam Kullanıcı" value={users.length} tone="primary" />
        <StatCard icon={<CheckCircle2 />} label="Aktif" value={activeCount} tone="success" />
        <StatCard icon={<XCircle />} label="Pasif" value={passiveCount} tone="danger" />
      </div>

      <section className="um-panel">
        <div className="um-toolbar">
          <div className="um-search">
            <Search />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Kullanıcı adı veya e-posta ara..."
            />
          </div>
          <div className="um-filters">
            <FilterChip active={statusFilter === 'all'} onClick={() => setStatusFilter('all')}>Tümü</FilterChip>
            <FilterChip active={statusFilter === 'active'} onClick={() => setStatusFilter('active')}>Aktif</FilterChip>
            <FilterChip active={statusFilter === 'passive'} onClick={() => setStatusFilter('passive')}>Pasif</FilterChip>
          </div>
        </div>

        <div className="um-table" role="table">
          <div className="um-table-head" role="row">
            <span />
            <span>Kullanıcı</span>
            <span>E-posta</span>
            <span>Rol</span>
            <span>Durum</span>
            <span className="um-col-actions">İşlem</span>
          </div>

          {filteredUsers.length === 0 && (
            <div className="um-empty">
              <Users />
              <strong>Eşleşen kullanıcı bulunamadı</strong>
              <span>Arama kriterlerini değiştirin ya da yeni bir kullanıcı oluşturun.</span>
            </div>
          )}

          {filteredUsers.map((user) => {
            const isExpanded = expandedId === user.id;
            return (
              <div className={`um-row-wrap${isExpanded ? ' is-expanded' : ''}`} key={user.id}>
                {/* Ana satır */}
                <div className="um-row" role="row">
                  <button
                    type="button"
                    className={`um-expand-btn${isExpanded ? ' is-active' : ''}`}
                    onClick={() => toggleExpand(user.id)}
                    title={isExpanded ? 'Daralt' : 'Genişlet'}
                  >
                    {isExpanded ? <ChevronUp /> : <ChevronDown />}
                  </button>
                  <div className="um-user-cell">
                    <div className="um-avatar">{getInitials(user.userName)}</div>
                    <div className="um-user-meta">
                      <strong>{user.userName}</strong>
                      <span>ID #{user.id}</span>
                    </div>
                  </div>
                  <div className="um-email-cell"><Mail /> {user.email}</div>
                  <div><span className="um-role">{user.role || 'User'}</span></div>
                  <div>
                    <span className={user.isActive ? 'um-status um-status-active' : 'um-status um-status-passive'}>
                      <span className="um-status-dot" />
                      {user.isActive ? 'Aktif' : 'Pasif'}
                    </span>
                  </div>
                  <div className="um-col-actions">
                    <button type="button" className="um-action-btn um-action-edit" title="Düzenle" onClick={() => openEdit(user)}>
                      <Pencil />
                    </button>
                    <button type="button" className="um-action-btn um-action-delete" title="Sil" onClick={() => setDeleteTarget(user)}>
                      <Trash2 />
                    </button>
                  </div>
                </div>

                {/* Genişletilmiş detay paneli */}
                {isExpanded && (
                  <div className="um-expand-panel">
                    <div className="um-expand-grid">
                      <div className="um-expand-field">
                        <span>Kullanıcı Adı</span>
                        <strong>{user.userName}</strong>
                      </div>
                      <div className="um-expand-field">
                        <span>E-posta</span>
                        <strong>{user.email}</strong>
                      </div>
                      <div className="um-expand-field">
                        <span>Rol</span>
                        <strong>{user.role || 'User'}</strong>
                      </div>
                      <div className="um-expand-field">
                        <span>Durum</span>
                        <strong className={user.isActive ? 'um-expand-active' : 'um-expand-passive'}>
                          {user.isActive ? 'Aktif' : 'Pasif'}
                        </strong>
                      </div>
                      {user.phone && (
                        <div className="um-expand-field">
                          <span>Telefon</span>
                          <strong>{user.phone}</strong>
                        </div>
                      )}
                      {user.address && (
                        <div className="um-expand-field um-expand-field-full">
                          <span>Adres</span>
                          <strong>{user.address}</strong>
                        </div>
                      )}
                      {user.createdAt && (
                        <div className="um-expand-field">
                          <span>Kayıt Tarihi</span>
                          <strong>{new Date(user.createdAt).toLocaleString('tr-TR')}</strong>
                        </div>
                      )}
                      <div className="um-expand-field">
                        <span>Bildirim Dili</span>
                        <strong>{LANGUAGE_LABELS[user.language] || 'Türkçe'}</strong>
                      </div>
                      {user.lastLogin && (
                        <div className="um-expand-field">
                          <span>Son Giriş</span>
                          <strong>{new Date(user.lastLogin).toLocaleString('tr-TR')}</strong>
                        </div>
                      )}
                    </div>
                    <div className="um-expand-actions">
                      <button type="button" className="btn btn-light" onClick={() => openEdit(user)}>
                        <Pencil /> Düzenle
                      </button>
                      <button type="button" className="um-btn-danger" onClick={() => setDeleteTarget(user)}>
                        <Trash2 /> Sil
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Kullanıcı oluşturma modalı */}
      {isCreateOpen && (
        <Modal title="Yeni Alt Kullanıcı" onClose={() => !submitting && setIsCreateOpen(false)}>
          <form className="um-form" onSubmit={submitCreate}>
            <p className="muted um-form-hint">
              Oluşturduğunuz kullanıcı yalnızca kendi müşteri kapsamındaki formları görür.
            </p>
            <div className="field">
              <label>Kullanıcı adı</label>
              <input
                required
                autoFocus
                value={newUser.userName}
                onChange={(e) => setNewUser({ ...newUser, userName: e.target.value })}
                placeholder="ornek.kullanici"
              />
            </div>
            <div className="field">
              <label>E-posta</label>
              <input
                required
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="ornek@firma.com"
              />
            </div>
            <div className="field">
              <label>Şifre</label>
              <input
                required
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="En az 6 karakter"
              />
            </div>
            <div className="field">
              <label>Telefon <span className="muted">(opsiyonel)</span></label>
              <input
                type="tel"
                value={newUser.phone}
                onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                placeholder="+90 555 000 00 00"
              />
            </div>
            <div className="field">
              <label>Adres <span className="muted">(opsiyonel)</span></label>
              <textarea
                value={newUser.address}
                onChange={(e) => setNewUser({ ...newUser, address: e.target.value })}
                placeholder="Açık adres"
                rows={2}
              />
            </div>
            <div className="field">
              <label>Bildirim dili</label>
              <select
                value={newUser.language}
                onChange={(e) => setNewUser({ ...newUser, language: e.target.value })}
              >
                <option value="tr">Türkçe</option>
                <option value="en">English</option>
                <option value="de">Deutsch</option>
              </select>
            </div>
            <div className="um-form-actions">
              <button type="button" className="btn btn-light" onClick={() => setIsCreateOpen(false)} disabled={submitting}>
                Vazgeç
              </button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                <UserPlus /> {submitting ? 'Ekleniyor...' : 'Kullanıcı Ekle'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Kullanıcı düzenleme modalı */}
      {editTarget && (
        <Modal title={`Düzenle — ${editTarget.userName}`} onClose={() => !submitting && setEditTarget(null)}>
          <form className="um-form" onSubmit={submitEdit}>
            <div className="um-edit-head">
              <div className="um-avatar um-avatar-lg">{getInitials(editTarget.userName)}</div>
              <div className="um-detail-head-meta">
                <strong>{editTarget.userName}</strong>
                <span className="muted">ID #{editTarget.id}</span>
              </div>
              <span className={editTarget.isActive ? 'um-status um-status-active' : 'um-status um-status-passive'}>
                <span className="um-status-dot" />
                {editTarget.isActive ? 'Aktif' : 'Pasif'}
              </span>
            </div>
            <div className="field">
              <label>Kullanıcı adı</label>
              <input
                required
                value={editForm.userName}
                onChange={(e) => setEditForm({ ...editForm, userName: e.target.value })}
              />
            </div>
            <div className="field">
              <label>E-posta</label>
              <input
                required
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </div>
            <div className="field">
              <label>Telefon <span className="muted">(opsiyonel)</span></label>
              <input
                type="tel"
                value={editForm.phone ?? ''}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                placeholder="+90 555 000 00 00"
              />
            </div>
            <div className="field">
              <label>Adres <span className="muted">(opsiyonel)</span></label>
              <textarea
                value={editForm.address ?? ''}
                onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                placeholder="Açık adres"
                rows={2}
              />
            </div>
            <div className="field">
              <label>Bildirim dili</label>
              <select
                value={editForm.language ?? 'tr'}
                onChange={(e) => setEditForm({ ...editForm, language: e.target.value })}
              >
                <option value="tr">Türkçe</option>
                <option value="en">English</option>
                <option value="de">Deutsch</option>
              </select>
            </div>
            <div className="field um-field-toggle">
              <label>Durum</label>
              <label className="um-toggle">
                <input
                  type="checkbox"
                  checked={editForm.isActive}
                  onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                />
                <span className="um-toggle-track"><span className="um-toggle-thumb" /></span>
                <span className="um-toggle-label">{editForm.isActive ? 'Aktif' : 'Pasif'}</span>
              </label>
            </div>

            {/* Şifre sıfırlama bölümü */}
            <div className="um-pw-section">
              {!showResetPw ? (
                <button type="button" className="um-pw-toggle-btn" onClick={() => setShowResetPw(true)}>
                  <KeyRound /> Şifre Sıfırla
                </button>
              ) : (
                <div className="um-pw-fields">
                  <div className="field" style={{ flex: 1 }}>
                    <label>Yeni Şifre</label>
                    <input
                      type="password"
                      value={newPassword}
                      autoFocus
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="En az 6 karakter"
                    />
                  </div>
                  <div className="um-pw-field-actions">
                    <button type="button" className="btn btn-primary" onClick={handleResetPassword} disabled={submitting}>
                      Kaydet
                    </button>
                    <button type="button" className="btn btn-light" onClick={() => { setShowResetPw(false); setNewPassword(''); }}>
                      İptal
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="um-form-actions">
              <button type="button" className="btn btn-light" onClick={() => setEditTarget(null)} disabled={submitting}>
                Vazgeç
              </button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                <Pencil /> {submitting ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Silme onay modalı */}
      <ConfirmModal
        isOpen={Boolean(deleteTarget)}
        type="danger"
        title="Kullanıcıyı Sil"
        message={`"${deleteTarget?.userName}" adlı kullanıcıyı kalıcı olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`}
        confirmText="Evet, Sil"
        cancelText="İptal"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Bildirim tostu */}
      <AlertModal
        isOpen={Boolean(alert)}
        type={alert?.type || 'info'}
        message={alert?.message || ''}
        onClose={() => setAlert(null)}
      />
    </div>
  );
}

function StatCard({ icon, label, value, tone }) {
  return (
    <div className={`um-stat um-stat-${tone}`}>
      <div className="um-stat-icon">{icon}</div>
      <div className="um-stat-body">
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button type="button" className={`um-chip${active ? ' is-active' : ''}`} onClick={onClick}>
      {children}
    </button>
  );
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/[\s._-]+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((part) => part[0]).join('');
  return letters.toUpperCase() || name[0].toUpperCase();
}
